import React, { useState, lazy, Suspense, useEffect } from 'react';
import Background from './Background/Background';
import Header from './Header/Header';
import Welcome from './Welcome/Welcome';
import ErrorBoundary from './ErrorBoundary';
import { Language } from '../types/i18n';

import './App.css';

const Content = lazy(() => import('./Content/Content'));

const App: React.FC = () => {
  const [mode, setMode] = useState<'night' | 'day' | ''>(() => {
    const stored = localStorage.getItem('theme') as 'night' | 'day' | null;
    if (stored) return stored;
    return window.matchMedia?.('(prefers-color-scheme: dark)').matches ? 'night' : 'day';
  });
  const [name, setName] = useState<string>('');
  const [pass, setPass] = useState<boolean>(() => {
    const hasCompleted = localStorage.getItem('hasCompletedWelcome') === 'true';
    const hasTheme = localStorage.getItem('theme');
    const hasLang = localStorage.getItem('language');
    return hasCompleted && !!hasTheme && !!hasLang;
  });
  const [lang, setLang] = useState<Language>(() => {
    const stored = localStorage.getItem('language') as Language;
    if (stored && ['en', 'es'].includes(stored)) return stored;
    const browserLang = navigator.language.split('-')[0];
    return browserLang === 'es' ? 'es' : 'en';
  });
  const cookie: string | null = document.cookie.split('name=')[1]
    ? document.cookie.split('name=')[1].split(';')[0]
    : null;

  useEffect(() => {
    console.log(`
███╗   ███╗ █████╗ ██╗    ██╗    ██████╗ ███████╗██╗   ██╗
████╗ ████║██╔══██╗██║    ██║    ██╔══██╗██╔════╝██║   ██║
██╔████╔██║███████║██║ █╗ ██║    ██║  ██║█████╗  ██║   ██║
██║╚██╔╝██║██╔══██║██║███╗██║    ██║  ██║██╔══╝  ╚██╗ ██╔╝
██║ ╚═╝ ██║██║  ██║╚███╔███╔╝ ██╗██████╔╝███████╗ ╚████╔╝
╚═╝     ╚═╝╚═╝  ╚═╝ ╚══╝╚══╝  ╚═╝╚═════╝ ╚══════╝  ╚═══╝
`);
    console.log(
      "%cHi! Nice to see you're curious too! :)",
      "background-color:black;color: white; font-family:'VG','Lucida Console', sans-serif; font-size: 24px; font-weight: bold;text-shadow: 3px 3px 0 rgb(217,31,38) , 6px 6px 0 rgb(226,91,14) , 9px 9px 0 rgb(245,221,8) , 12px 12px 0 rgb(5,148,68) , 15px 15px 0 rgb(2,135,206) , 18px 18px 0 rgb(4,77,145) , 21px 21px 0 rgb(42,21,113)');"
    );
    if (cookie && name === '') {
      setName(document.cookie.split('name=')[1].split(';')[0]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (pass && name !== '' && cookie !== name) {
      document.cookie = `name=${name}`;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pass]);

  useEffect(() => {
    localStorage.setItem('language', lang);
    document.documentElement.lang = lang;
  }, [lang]);

  useEffect(() => {
    if (mode) localStorage.setItem('theme', mode);
  }, [mode]);

  useEffect(() => {
    if (pass) localStorage.setItem('hasCompletedWelcome', 'true');
  }, [pass]);

  const handleShowWelcome = () => {
    setPass(false);
  };

  return (
    <ErrorBoundary>
      <div id="rotate-notice">
        <span className="rotate-icon">📱</span>
        <span>{lang === 'es' ? 'Por favor, gira tu dispositivo a modo vertical' : 'Please rotate your device to portrait mode'}</span>
      </div>
      <div id="mawmawmaw" className={mode}>
        <Background mode={mode} />
        <Header />
        {pass || (
          <Welcome name={name} setName={setName} setPass={setPass} mode={mode} setMode={setMode} lang={lang} setLang={setLang} />
        )}
        {!pass || (
          <Suspense
            fallback={
              <div id="loader">
                <i className="spinner icon"></i>
              </div>
            }
          >
            <ErrorBoundary>
              <Content
                name={name}
                mode={mode}
                lang={lang}
                setMode={setMode}
                setLang={setLang}
                setName={setName}
                onShowWelcome={handleShowWelcome}
              />
            </ErrorBoundary>
          </Suspense>
        )}
      </div>
    </ErrorBoundary>
  );
};

export default App;
